/**
 * useEquipment — React hook for the local player's equipped gear.
 *
 * Event-driven: the server includes equipment ids in every inventory snapshot;
 * `InventorySystem` resolves those ids to canonical item definitions and emits
 * `snapshotUpdated`. We seed from `getAllEquippedItems()` synchronously in the
 * first render (an already-hydrated system must never flash empty slots on
 * remount — CHAR-MUT-REOPEN-01), then re-snapshot the full slot view on every
 * authoritative update. No polling.
 *
 * `equip(itemId)` / `unequip(slotId)` emit server action intents. The window
 * waits for the resulting inventory snapshot instead of mutating a client-only
 * equipment map that the next server frame would overwrite.
 *
 * This REPLACES EquipmentWindow's hardcoded `equippedItems: {}` mock with real
 * data from the live InventorySystem.
 *
 * Slot keys come from the canonical `EQUIPMENT_SLOTS` list the EquipmentManager
 * itself initializes from, so `slots` always has an entry (item-or-null) for
 * every real slot — empty slots render as empty rather than disappearing.
 */

import { useCallback, useEffect, useState } from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { InventorySystem } from '../../systems/inventory/InventorySystem';
import type { InventoryItem } from '../../../shared/CharacterTypes';
import { EQUIPMENT_SLOTS } from '../../../shared/content/generated/items.gen';
import type { ItemData } from '../components/ItemKit';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { ITEM_SLOT_TO_EQUIPMENT_SCHEMA_SLOT, type ItemEquipmentSlot } from '../../../shared/equipmentSlots';

/** Events the InventorySystem emits (via `on('<event>', cb)`) for equipment. */
const EQUIPMENT_EVENTS = ['snapshotUpdated', 'itemEquipped', 'itemUnequipped'] as const;

/** Map of slotId → equipped item (or null when the slot is empty). */
export type EquipmentSlots = Record<string, ItemData | null>;

export interface EquipmentState {
  /** One entry per canonical equipment slot; null when nothing is equipped. */
  slots: EquipmentSlots;
}

export interface EquipmentActions {
  /** Equip an inventory item by id through the server `useItem` handler. */
  equip: (itemId: string) => void;
  /** Unequip a slot through the server-authoritative inventory handler. */
  unequip: (slotId: string) => void;
}

function toNumericStats(stats: InventoryItem['stats']): Record<string, number> | undefined {
  if (!stats) return undefined;
  return Object.fromEntries(
    Object.entries(stats).filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
  );
}

/** Resolve the InventorySystem singleton without throwing if unregistered. */
function getInventorySystem(): InventorySystem | undefined {
  try {
    return ServiceRegistry.getInstance().get('InventorySystem', { optional: true });
  } catch {
    return undefined;
  }
}

/**
 * Project an `InventoryItem` onto the `ItemData` shape ItemKit renders.
 * Exported for unit testing — the rarity domains differ between the two types
 * (`InventoryItem` has `'unique'`, `ItemData` has `'legendary'`), so the mapping
 * is the one piece worth pinning down.
 */
export function toItemData(item: InventoryItem): ItemData {
  // `'unique'` is the InventoryItem top rarity; ItemKit's top tier is
  // `'legendary'` — alias it so unique gear still gets the brightest glow.
  const rarity = item.rarity === 'unique' ? 'legendary' : item.rarity;
  return {
    id: item.id,
    name: item.name,
    icon: item.icon,
    description: item.description,
    rarity,
    quantity: item.quantity,
    type: item.type,
    stats: toNumericStats(item.stats),
  };
}

/** Build the full slot map (empty slots included) from the equipped item map. */
function buildSlots(equipped: ReadonlyMap<string, InventoryItem>): EquipmentSlots {
  const slots: EquipmentSlots = {};
  for (const slotId of EQUIPMENT_SLOTS) {
    const item = equipped.get(slotId);
    slots[slotId] = item ? toItemData(item) : null;
  }
  return slots;
}

export function useEquipment(): EquipmentState & EquipmentActions {
  const [slots, setSlots] = useState<EquipmentSlots>(() => {
    const sys = getInventorySystem();
    return buildSlots(sys ? sys.getAllEquippedItems() : new Map());
  });

  useEffect(() => {
    // A persisted-open window mounts at page boot, BEFORE GameScene registers
    // InventorySystem — a one-shot resolve here would silently never subscribe
    // and freeze the slots empty forever (CHAR-MUT-RECONNECT-01). Arm against
    // the current system now when it exists, and re-arm on every
    // GAME_SCENE_READY (emitted strictly after system registration), which
    // also rebinds across same-page scene reboots.
    let unsubs: Array<() => void> = [];
    const arm = () => {
      for (const unsub of unsubs) unsub();
      unsubs = [];
      const sys = getInventorySystem();
      if (!sys) return;
      const snapshot = () => setSlots(buildSlots(sys.getAllEquippedItems()));
      snapshot();
      unsubs = EQUIPMENT_EVENTS.map((event) => sys.on(event, snapshot));
    };
    arm();
    const unsubReady = EventBus.getInstance().on(EVENTS.GAME_SCENE_READY, arm);
    return () => {
      unsubReady();
      for (const unsub of unsubs) unsub();
    };
  }, []);

  const equip = useCallback((itemId: string) => {
    EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
      action: 'useItem',
      data: { itemId },
    });
  }, []);

  const unequip = useCallback((slotId: string) => {
    const schemaSlot = ITEM_SLOT_TO_EQUIPMENT_SCHEMA_SLOT[slotId as ItemEquipmentSlot];
    if (!schemaSlot) return;
    EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
      action: 'unequipItem',
      data: { slotId: schemaSlot },
    });
  }, []);

  return { slots, equip, unequip };
}
